import React from 'react';
import ReactDOM from 'react-dom';
import FeatureInfo from '../app/components/map/FeatureInfo';
import { TerraDrawManager } from './TerraDrawManager';
import { MuiThemeProvider } from '@material-ui/core';
import theme from '../theme';
import { createShorelineTheme } from '../app/pages/Shoreline/theme.js';
import { aoiPaths, aoiPoints, isMultiPolygon } from './aoi';

let Map = undefined;
const STYLE_KEY = 'customStyle';
const HIGHLIGHTED_KEY = 'highlighted';

let shapeId = 0;
let infoWindow;

/**
 * @function getInfoWindowThemeMode
 * @description Reads the shared light/dark preference (`ThemeModeContext`'s `shorelineThemeMode`,
 * persisted under the same localStorage key by `useLocalStorage`) directly from localStorage. The
 * map-click InfoWindow popups (`FeatureInfo`/`TransectEvolution`) are mounted via a standalone
 * `ReactDOM.render` call into a Google Maps InfoWindow DOM node, outside the app's own React tree —
 * so no ancestor `ThemeModeProvider`/`ThemeProvider` context reaches them, and the mode has to be
 * read directly rather than via `useThemeMode()`.
 * **Called by:
 * @see {Map.data.addListener} - transect click handler, to theme the InfoWindow's `FeatureInfo`.
 * @see {addLayer} - polygon shape click handler, to theme the InfoWindow's `FeatureInfo`.
 * @memberof module:SHARED
 * @returns {'light'|'dark'} The current theme mode.
 */
const getInfoWindowThemeMode = () => {
  try {
    return JSON.parse(window.localStorage.getItem('shorelineThemeMode')) || 'light';
  } catch (error) {
    return 'light';
  }
};

export const extractTransectDataFromMap = () => {
  const transectMatrix = [];
  const minmaxResults = { minValue: Infinity, maxValue: -Infinity };
  let featureIndex = 0;

  Map.data.forEach((feature) => {
    const type = feature.getProperty('featureType');

    if (type === 'transect') {
      let distances = feature.getProperty('distances');

      if (typeof distances === 'string') {
        try {
          distances = JSON.parse(distances.replace(/^"+|"+$/g, ''));
        } catch (e) {
          console.log(`Erro ao parsear distances da feature #${featureIndex}:`, distances);
          distances = [];
        }
      }

      if (Array.isArray(distances) && distances.length > 0) {
        const id = feature.getProperty('id') || `transect-${featureIndex}`;
        const measurement = feature.getProperty('cassie:internals')?.measurement;

        if (measurement && typeof measurement === 'object') {
          const distancesWithDates = Object.values(measurement).map((entry) => {
            const timestamp = entry.date?.value;
            const date = timestamp ? new Date(timestamp).toISOString() : null;
            return {
              date,
              distance: entry.distance,
            };
          });

          transectMatrix.push({ id, distances: distancesWithDates });
        }

        const localMax = Math.max(...distances);
        const localMin = Math.min(...distances);

        if (localMax > minmaxResults.maxValue) {
          minmaxResults.maxValue = localMax;
        }

        if (localMin < minmaxResults.minValue) {
          minmaxResults.minValue = localMin;
        }
      } else {
        console.warn(`Feature #${featureIndex} não possui distances válidas`);
      }
    }

    featureIndex++;
  });

  if (Map.nativeOverlays) {
    Map.nativeOverlays.forEach((overlay) => {
      if (overlay.featureType === 'transect' && overlay.geoJson && overlay.geoJson.properties) {
        const props = overlay.geoJson.properties;
        let distances = props.distances;

        if (typeof distances === 'string') {
          try {
            distances = JSON.parse(distances.replace(/^"+|"+$/g, ''));
          } catch (e) {
            console.log(`Erro ao parsear distances do overlay #${featureIndex}:`, distances);
            distances = [];
          }
        }

        if (Array.isArray(distances) && distances.length > 0) {
          const id = props.id || `transect-${featureIndex}`;
          const measurement = props['cassie:internals']?.measurement;

          if (measurement && typeof measurement === 'object') {
            const distancesWithDates = Object.values(measurement).map((entry) => {
              const timestamp = entry.date?.value;
              const date = timestamp ? new Date(timestamp).toISOString() : null;
              return {
                date,
                distance: entry.distance,
              };
            });

            transectMatrix.push({ id, distances: distancesWithDates });
          }

          const localMax = Math.max(...distances);
          const localMin = Math.min(...distances);

          if (localMax > minmaxResults.maxValue) {
            minmaxResults.maxValue = localMax;
          }

          if (localMin < minmaxResults.minValue) {
            minmaxResults.minValue = localMin;
          }
        }
      }

      featureIndex++;
    });
  }

  return { transectMatrix, minmaxResults };
};

export const initializeMap = (map) => {
  infoWindow = new window.google.maps.InfoWindow({ content: 'Hello world!' });
  Map = map;
  console.log('MAP-initializeMap:', map);
  window.initMap = map;
  Map.customLayers = [];
  Map.nativeOverlays = [];
  Map.customControls = [];

  const originalOverrideStyle = Map.data.overrideStyle.bind(Map.data);
  Map.data.overrideStyle = function (feature, style) {
    if (
      (feature && feature.overlay && feature.overlay instanceof window.google.maps.Polyline) ||
      (feature && feature.overlay && feature.overlay instanceof window.google.maps.Polygon)
    ) {
      const overlay = feature.overlay;
      const geoJson = overlay.geoJson;

      if (style) {
        const options = {};

        if (style.strokeOpacity !== undefined) {
          options.strokeOpacity = style.strokeOpacity;
        }
        if (style.fillOpacity !== undefined) {
          options.fillOpacity = style.fillOpacity;
        }
        if (style.strokeColor !== undefined) {
          options.strokeColor = style.strokeColor;
        }
        if (style.fillColor !== undefined) {
          options.fillColor = style.fillColor;
        }
        if (style.strokeWeight !== undefined) {
          options.strokeWeight = style.strokeWeight;
        }

        overlay.cassie_override_style = style;

        overlay.setOptions(options);
      }

      return;
    }

    return originalOverrideStyle(feature, style);
  };

  Map.data.setStyle((feature) => {
    const style = feature.getProperty(STYLE_KEY);
    if (feature.getProperty(HIGHLIGHTED_KEY) === true) {
      return { ...style, strokeWeight: style.strokeWeight + 4 };
    }

    return style;
  });

  window.google.maps.event.addListenerOnce(Map.data, 'addfeature', () => {});

  Map.imageOutline = undefined;

  const shapeOptions = {
    fillColor: '#00B3A1',
    fillOpacity: 0.2,
    strokeColor: '#003832',
    strokeOpacity: 1,
    strokeWeight: 0.5,
  };

  Map.drawingManager = new TerraDrawManager({
    drawingMode: null,
    drawingControl: false,
    drawingControlOptions: {
      position: window.google.maps.ControlPosition.TOP_CENTER,
      drawingModes: ['polygon', 'rectangle', 'polyline'],
    },
    polygonOptions: {
      ...shapeOptions,
      clickable: false,
    },
    rectangleOptions: {
      ...shapeOptions,
      clickable: false,
    },
    polylineOptions: {
      strokeColor: '#00B3A1',
      strokeOpacity: 1,
      strokeWeight: 2.75,
      geodesic: true,
      clickable: false,
    },
  });

  Map.drawingManager.setMap(Map);

  Map.drawingManager.addListener('overlaycomplete', (e) => {
    const overlay = e.overlay;
    const type = e.type;

    Map.nativeOverlays = Map.nativeOverlays || [];
    Map.nativeOverlays.push(overlay);

    if (Map.regionDrawnHandler) {
      const coords = toCoordinates(extractCoordinates(overlay, type));
      Map.regionDrawnHandler(overlay, coords, type);
    }

    const { transectMatrix, minmaxResults } = extractTransectDataFromMap();
    Map.transectMatrix = transectMatrix;
    Map.minmaxResults = minmaxResults;

    if (!transectMatrix || transectMatrix.length === 0) {
      console.log('Desenho concluído, mas sem transectos válidos.');
    } else {
      console.log('Matriz de transectos e distâncias:', transectMatrix);
      console.log('Resultado final min/max:', minmaxResults);
    }

    if (overlay && typeof overlay.setMap === 'function') {
      overlay.setMap(Map);
    }
  });

  Map.addListener('zoom_changed', () => {
    if (Map.zoomChangeHandler) {
      Map.zoomChangeHandler(Map.getZoom());
    }
  });

  Map.data.addListener('click', (event) => {
    if (event.placeId) {
      event.stop();
      return;
    }

    const isDrawing =
      (Map.drawingManager &&
        ((typeof Map.drawingManager.getIsDrawing === 'function' && Map.drawingManager.getIsDrawing()) ||
          (typeof Map.drawingManager.getDrawingMode === 'function' && Map.drawingManager.getDrawingMode() !== null) ||
          (typeof Map.drawingManager.getMode === 'function' && Map.drawingManager.getMode() !== null) ||
          Map.drawingManager.isDrawing === true ||
          Map.drawingManager.currentMode)) ||
      false;

    if (isDrawing) {
      console.log('User is drawing, ignoring click event');
      return;
    }

    const properties = {};
    const { transectMatrix, minmaxResults } = extractTransectDataFromMap();

    if (!transectMatrix || transectMatrix.length === 0) {
      console.log('Nenhum transecto finalizado — não abrindo InfoWindow.');
      return;
    }

    Map.transectMatrix = transectMatrix;
    Map.minmaxResults = minmaxResults;

    console.log('Matriz de transectos e distâncias:', transectMatrix);
    console.log('Resultado final min/max:', minmaxResults);

    if (event.feature && typeof event.feature.forEachProperty === 'function') {
      event.feature.forEachProperty((value, name) => {
        properties[name] = value;
      });
    }

    infoWindow.setPosition(event.latLng);
    infoWindow.setContent(`<div id='infowindow'>hello world!</div>`);
    infoWindow.open(Map);

    window.google.maps.event.addListenerOnce(infoWindow, 'domready', () => {
      ReactDOM.render(
        <MuiThemeProvider theme={createShorelineTheme(getInfoWindowThemeMode(), theme)}>
          <FeatureInfo minmaxResults={minmaxResults} data={properties} />
        </MuiThemeProvider>,
        document.getElementById('infowindow'),
      );
    });

    setDrawingControlsVisible(false);
  });
};

export const addLayer = (overlay, index) => {
  Map.customLayers.splice(index, 0, overlay);
  Map.overlayMapTypes.insertAt(index, overlay);

  return index;
};

/**
 * Because of how customLayers works, each time one layer is deleted,
 * the others change the index and the reference ends up being lost.
 *
 * So I ended up choosing to just remove the references to this layer in the interface
 * and make it transparent.
 **/
export const removeLayer = (index) => {
  Map.customLayers[index].setOpacity(0);
  // Map.customLayers.splice(index, 1);
  // Map.overlayMapTypes.removeAt(index);
};

export const replaceLayer = (index, overlay) => {
  Map.customLayers[index] = overlay;
  Map.overlayMapTypes.setAt(index, overlay);
};

// Expects an array of coordinates of the form [lng, lat].
export const addPolygon = (coordinates) => {
  Map.data.add({
    geometry: new window.google.maps.Data.Polygon([toLatLng(coordinates)]),
  });
};

export const addShape = (geoJson, color = '#FF0000', opacity = 0.5, type) => {
  const uid = shapeId++;

  let overlay = null;

  if (geoJson.geometry.type === 'LineString') {
    const path = geoJson.geometry.coordinates.map((coord) => ({
      lat: coord[1],
      lng: coord[0],
    }));

    overlay = new window.google.maps.Polyline({
      path: path,
      strokeColor: color,
      strokeOpacity: 1,
      strokeWeight: 1.666,
      map: Map,
      geodesic: false,
    });

    const hitPolyline = new window.google.maps.Polyline({
      path: path,
      strokeOpacity: 0,
      strokeWeight: 15,
      map: Map,
      zIndex: 10,
    });

    const hoverDiv = document.createElement('div');
    hoverDiv.style.cssText =
      'position:fixed;background:#fff;border:1px solid #ccc;border-radius:4px;' +
      'padding:4px 8px;font-size:12px;line-height:1.6;pointer-events:none;' +
      'white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,.2);display:none;z-index:9999';
    document.body.appendChild(hoverDiv);

    hitPolyline.addListener('mouseover', (e) => {
      const props = geoJson.properties || {};

      const rawDate = props.dateTime ?? props.date ?? props.datetime;
      let dateObj = null;
      if (rawDate) {
        if (typeof rawDate === 'object' && rawDate.value != null) {
          dateObj = new Date(rawDate.value);
        } else {
          dateObj = new Date(rawDate);
        }
      }
      const date =
        dateObj && !isNaN(dateObj)
          ? dateObj.toLocaleString('pt-BR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })
          : '—';

      hoverDiv.innerHTML = `<strong>${date}</strong>`;
      hoverDiv.style.display = 'block';
      overlay.setOptions({ strokeWeight: 4 });
    });

    hitPolyline.addListener('mousemove', (e) => {
      const domEvent = e.domEvent;
      if (domEvent) {
        hoverDiv.style.left = `${domEvent.clientX + 12}px`;
        hoverDiv.style.top = `${domEvent.clientY - 28}px`;
      }
    });

    hitPolyline.addListener('mouseout', () => {
      hoverDiv.style.display = 'none';
      overlay.setOptions({ strokeWeight: 1.666 });
    });

    overlay.hitArea = hitPolyline;
  } else if (geoJson.geometry.type === 'Polygon') {
    const paths = geoJson.geometry.coordinates[0].map((coord) => ({
      lat: coord[1],
      lng: coord[0],
    }));

    overlay = new window.google.maps.Polygon({
      paths: paths,
      strokeColor: color,
      strokeOpacity: opacity,
      strokeWeight: 0.666,
      fillColor: color,
      fillOpacity: opacity * 0.3,
      map: Map,
      geodesic: false,
    });

    if (type === 'transect' && geoJson.properties?.label) {
      const hoverDiv = document.createElement('div');
      hoverDiv.style.cssText =
        'position:fixed;background:#fff;border:1px solid #ccc;border-radius:4px;' +
        'padding:4px 8px;font-size:12px;line-height:1.6;pointer-events:none;' +
        'white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,.2);display:none;z-index:9999';
      document.body.appendChild(hoverDiv);

      overlay.addListener('mouseover', () => {
        hoverDiv.innerHTML = `<strong>${geoJson.properties.label}</strong>`;
        hoverDiv.style.display = 'block';
      });
      overlay.addListener('mousemove', (e) => {
        const domEvent = e.domEvent;
        if (domEvent) {
          hoverDiv.style.left = `${domEvent.clientX + 12}px`;
          hoverDiv.style.top = `${domEvent.clientY - 28}px`;
        }
      });
      overlay.addListener('mouseout', () => {
        hoverDiv.style.display = 'none';
      });
    }
  }

  if (overlay) {
    overlay.geoJson = geoJson;
    overlay.uid = uid;
    overlay.featureType = type;
    overlay.cassie_color = color;
    overlay.cassie_opacity = opacity;
    overlay.cassie_override_style = null;

    Map.nativeOverlays.push(overlay);

    const shapeWrapper = {
      overlay: overlay,
      uid: uid,
      featureType: type,
      getProperty: (key) => {
        if (key === 'featureType') return type;
        if (key === 'customStyle' || key === STYLE_KEY)
          return {
            fillOpacity: opacity * 0.3,
            fillColor: color,
            strokeWeight: geoJson.geometry.type === 'LineString' ? 1.666 : 0.666,
            strokeColor: color,
            strokeOpacity: geoJson.geometry.type === 'LineString' ? 1 : opacity,
          };
        if (key === HIGHLIGHTED_KEY) return overlay.cassie_highlighted || false;
        return geoJson.properties ? geoJson.properties[key] : undefined;
      },
      setProperty: (key, value) => {
        if (key === HIGHLIGHTED_KEY) {
          overlay.cassie_highlighted = value;
          if (value) {
            overlay.setOptions({
              strokeWeight: (geoJson.geometry.type === 'LineString' ? 1.666 : 0.666) + 4,
            });
          } else {
            overlay.setOptions({
              strokeWeight: geoJson.geometry.type === 'LineString' ? 1.666 : 0.666,
            });
          }
        }
        if (key === 'customStyle' || key === STYLE_KEY) {
          geoJson.properties = geoJson.properties || {};
          geoJson.properties[key] = value;
        }
      },
      forEachProperty: (callback) => {
        if (geoJson.properties) {
          Object.keys(geoJson.properties).forEach((key) => {
            callback(geoJson.properties[key], key);
          });
        }
      },
      getGeometry: () => geoJson.geometry,
    };

    window.google.maps.event.addListener(overlay, 'click', function (event) {
      if (event.placeId) {
        event.stop();
      }
      if (Map.drawingManager && Map.drawingManager.getIsDrawing && Map.drawingManager.getIsDrawing()) {
        console.log('User is drawing, ignoring click event');
        return;
      }

      if (type === 'transect') {
        const properties = {};
        const { transectMatrix, minmaxResults } = extractTransectDataFromMap();

        Map.transectMatrix = transectMatrix;
        Map.minmaxResults = minmaxResults;

        console.log('Matriz de transectos e distâncias:', transectMatrix);
        console.log('Resultado final min/max:', minmaxResults);

        shapeWrapper.forEachProperty((value, name) => {
          properties[name] = value;
        });

        properties.featureType = type;

        infoWindow.setPosition(event.latLng);
        infoWindow.setContent(`<div id='infowindow'></div>`);
        infoWindow.open(Map);

        window.google.maps.event.addListenerOnce(infoWindow, 'domready', () => {
          ReactDOM.render(
            <MuiThemeProvider theme={createShorelineTheme(getInfoWindowThemeMode(), theme)}>
              <FeatureInfo minmaxResults={minmaxResults} data={properties} />
            </MuiThemeProvider>,
            document.getElementById('infowindow'),
          );
        });

        setDrawingControlsVisible(false);
      }
    });

    return shapeWrapper;
  }

  return null;
};

export const highlightShape = (shape) => {
  shape.setProperty(HIGHLIGHTED_KEY, true);
};

export const clearHighlight = (shape) => {
  shape.setProperty(HIGHLIGHTED_KEY, false);
};

export const overrideShapeStyle = (shape, style) => {
  if (!Map || !Map.data) return;
  Map.data.overrideStyle(shape, style);
};

export const removeShape = (shape) => {
  if (shape.overlay) {
    if (shape.overlay.hitArea) shape.overlay.hitArea.setMap(null);
    shape.overlay.setMap(null);
    const index = Map.nativeOverlays.indexOf(shape.overlay);
    if (index > -1) Map.nativeOverlays.splice(index, 1);
    return;
  }
  Map.data.remove(shape);
};

export const centralize = (lat, lng) => {
  Map.setCenter({ lat, lng });
};

/**
 * @constant FIT_PADDING
 * @description Space, in px, left between a framed area and the edges of the map, so an area that
 * reaches the viewport's border still reads as a shape rather than as a cut-off line.
 */
const FIT_PADDING = 40;

/**
 * @function fitToCoordinates
 * @description Frames a set of coordinates so the whole of it is on screen.
 * An imported area used to be centred on the first vertex of its ring, at whatever zoom the map
 * happened to be showing: a KML loaded onto a world view left the area an invisible speck, and one
 * loaded onto a close view put a single corner in the middle with the rest of the area off screen.
 * The area is known in full at that point, so the map can be framed to it instead.
 * Falls back to centring when every coordinate is the same point - a box with no size sends Google
 * Maps to its maximum zoom, which lands the user somewhere inside a building.
 * @param {Array} coordinates - Points as `[lng, lat]` pairs, in the order the KML/GeoJSON ring
 *  gives them, the ring not needing to be closed; or an area of several polygons, framed whole.
 * @param {Number} [padding=FIT_PADDING] - Space to leave around the area, in px.
 * **Called by:
 * @see {AOIChooser} - every module's area step, on a KML import and when an area is restored.
 * @see {GoogleMap} - the shared KML import panel.
 * @returns {void}
 */
export const fitToCoordinates = (coordinates, padding = FIT_PADDING) => {
  if (!Map || !Array.isArray(coordinates) || coordinates.length === 0) return;

  const bounds = new window.google.maps.LatLngBounds();
  aoiPoints(coordinates).forEach((point) => {
    const [lng, lat] = point || [];
    if (Number.isFinite(lat) && Number.isFinite(lng)) bounds.extend({ lat, lng });
  });

  if (bounds.isEmpty()) return;

  if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
    const center = bounds.getCenter();
    Map.setCenter({ lat: center.lat(), lng: center.lng() });
    return;
  }

  Map.fitBounds(bounds, padding);
};

/**
 * Drops a ring's repeated closing coordinate, when it has one. GeoJSON and KML
 * rings repeat their first coordinate at the end; Google Maps closes polygons on
 * its own, so keeping the repeat renders two overlapping vertex handles at the
 * first vertex as soon as the polygon is made editable.
 * An area of several polygons is returned as it is: its rings are opened when the KML is read.
 * @param {Array<Array<Number>>} coordinates ring as `[lng, lat]` pairs
 * @returns {Array<Array<Number>>} the ring without its closing coordinate
 */
export const toOpenRing = (coordinates) => {
  if (!Array.isArray(coordinates) || coordinates.length < 2 || isMultiPolygon(coordinates)) return coordinates;

  const first = coordinates[0];
  const last = coordinates[coordinates.length - 1];

  return first[0] === last[0] && first[1] === last[1] ? coordinates.slice(0, -1) : coordinates;
};

export const drawOutline = (coordinates) => {
  clearOutline();

  Map.imageOutline = new window.google.maps.Polygon({
    paths: isMultiPolygon(coordinates) ? aoiPaths(coordinates) : toLatLng(toOpenRing(coordinates)),
    fillOpacity: 0,
    strokeWeight: 3,
    strokeColor: '#464646',
    strokeOpacity: 0.8,
  });

  Map.imageOutline.cassie_shape_type = 'polygon';
  Map.imageOutline.setMap(Map);
  return Map.imageOutline;
};

export const drawCircle = (coordinates) => {
  return coordinates.map((point) => {
    //console.log("point v2:", point);
    // Add the circle for this city to the map.
    const circle = new window.google.maps.Circle({
      strokeColor: '#FF0000',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#000000',
      fillOpacity: 0.35,
      map: Map,
      center: { lat: point[1], lng: point[0] },
      radius: 6,
    });
    return circle;
  });
};

export const drawOutlinePoints = (coordinates) => {
  Map.imageOutline = new window.google.maps.Polygon({
    paths: toLatLng(coordinates),
    fillOpacity: 0,
    strokeWeight: 1,
    strokeColor: '#1DAB6E',
    strokeOpacity: 0.8,
  });

  Map.imageOutline.setMap(Map);
  return Map.imageOutline;
};

export const clearOutline = () => {
  if (Map.imageOutline) {
    Map.imageOutline.setMap(null);
  }
};

export const setOpacity = (layer, value) => {
  if (Map.customLayers[layer] !== undefined) {
    Map.customLayers[layer].setOpacity(value);
  } else {
    console.warn(`Layer ${layer} is not defined`);
  }
};

export const toLatLng = (coordinates) => {
  return coordinates.map((coord) => ({ lat: coord[1], lng: coord[0] }));
};

export const toCoordinates = (latLngArray) => {
  return latLngArray.map((value) => [value.lng, value.lat]);
};

export const loadGeoJSON = (data) => {
  return Map.data.addGeoJson(data);
};

export const loadWRSLayer = () => {
  const layer = new window.google.maps.FusionTablesLayer({
    map: Map,
    query: {
      select: 'geometry',
      from: process.env.API_KEY,
    },
    styles: [
      {
        polygonOptions: {
          fillOpacity: 0.03,
          strokeOpacity: 0.5,
          strokeWeight: 0.5,
        },
      },
    ],
  });

  return layer;
};

export const getZoomLevel = () => {
  return Map.getZoom();
};

export const displayElement = (element) => {
  element.setMap(Map);
};

export const hideElement = (element) => {
  element.setMap(null);
};

export const onZoomChange = (handler) => {
  Map.zoomChangeHandler = handler;
};

export const onRegionDrawn = (handler) => {
  Map.regionDrawnHandler = handler;
};

export const setDrawingControlsVisible = (visible) => {
  Map.drawingManager.setOptions({
    drawingControl: visible,
  });

  if (visible) {
   moveCustomControlsToEnd();
  } else {
    setDrawingMode(null);
  }
};

export const setDrawingMode = (mode) => {
  Map.drawingManager.setDrawingMode(mode);
};

/**
 * Tells apart the three overlay shapes the drawing manager produces, so callers
 * that only hold the overlay can still read its coordinates back. Overlays built
 * by `TerraDrawManager` and `drawOutline` carry the type on themselves; anything
 * else falls back to the Google Maps class.
 * @param {google.maps.Polygon|google.maps.Rectangle|google.maps.Polyline} overlay
 * @returns {('polygon'|'rectangle'|'polyline'|undefined)}
 */
export const getOverlayType = (overlay) => {
  if (!overlay) return undefined;
  if (overlay.cassie_shape_type) return overlay.cassie_shape_type;
  if (overlay instanceof window.google.maps.Rectangle) return 'rectangle';
  if (overlay instanceof window.google.maps.Polyline) return 'polyline';

  return 'polygon';
};

/**
 * Reads an overlay's current coordinates as `[lng, lat]` pairs — the same shape
 * `onRegionDrawn` hands out, so an edited overlay can feed the exact same state.
 * @param {google.maps.Polygon|google.maps.Rectangle|google.maps.Polyline} overlay
 * @param {('polygon'|'rectangle'|'polyline')} [type] defaults to `getOverlayType(overlay)`
 * @returns {Array<Array<Number>>}
 */
export const getOverlayCoordinates = (overlay, type) => {
  return toCoordinates(extractCoordinates(overlay, type || getOverlayType(overlay)));
};

/**
 * Mounts a caller-owned element as one of the map's own controls, so a button
 * can sit on the map instead of around it.
 * @param {HTMLElement} element
 * @param {Number} [position] a `google.maps.ControlPosition`, defaulting to `TOP_CENTER`
 * @returns {Function} removes the control again, to be called before dropping the element
 */
export const addCustomControl = (element, position) => {
  const controlPosition = position ?? window.google.maps.ControlPosition.TOP_CENTER;

  Map.customControls = Map.customControls || [];
  Map.customControls.push({ element, position: controlPosition });
  Map.controls[controlPosition].push(element);

  return () => {
    Map.customControls = (Map.customControls || []).filter((control) => control.element !== element);

    const controls = Map.controls[controlPosition];
    const index = controls.getArray().indexOf(element);
    if (index > -1) controls.removeAt(index);
  };
};

/**
 * Re-appends every control added through `addCustomControl`, so they keep sitting
 * after the map's built-in controls at the same position. Google Maps lays a
 * position's controls out in the order they were added, and the drawing tools
 * re-add themselves every time they are shown.
 * @see {@link setDrawingControlsVisible}
 * @returns {void}
 */
const moveCustomControlsToEnd = () => {
  (Map.customControls || []).forEach(({ element, position }) => {
    const controls = Map.controls[position];
    const index = controls.getArray().indexOf(element);
    if (index > -1) controls.removeAt(index);
    controls.push(element);
  });
};

/**
 * Lets the user drag an already-drawn shape's existing vertices, and only those:
 * Google Maps' own `editable` mode always comes with midpoint handles that add
 * new vertices, so the handles are drawn here as plain draggable markers instead.
 *
 * Dragging a rectangle's corner resizes it around the opposite corner, keeping it
 * a rectangle; polygons and polylines move the single vertex being dragged.
 * @param {google.maps.Polygon|google.maps.Rectangle|google.maps.Polyline} overlay
 * @param {('polygon'|'rectangle'|'polyline')} [type] defaults to `getOverlayType(overlay)`
 * @param {Function} handler receives the updated `[lng, lat]` pairs after each drag
 * @returns {Function} removes the handles, to be called when editing stops
 */
export const enableVertexEditing = (overlay, type, handler) => {
  if (!overlay) return () => {};

  const shapeType = type || getOverlayType(overlay);
  const isRectangle = shapeType === 'rectangle';
  const markers = [];

  const vertexPositions = () =>
    isRectangle
      ? extractCoordinates(overlay, 'rectangle').map((corner) => new window.google.maps.LatLng(corner.lat, corner.lng))
      : overlay.getPath().getArray();

  const syncMarkers = (draggedIndex) => {
    const positions = vertexPositions();
    markers.forEach((marker, index) => {
      if (index !== draggedIndex && positions[index]) marker.setPosition(positions[index]);
    });
  };

  const moveVertex = (index, position) => {
    if (!isRectangle) {
      overlay.getPath().setAt(index, position);
      return;
    }

    const corners = extractCoordinates(overlay, 'rectangle');
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(corners[(index + 2) % corners.length]);
    bounds.extend(position);
    overlay.setBounds(bounds);
  };

  vertexPositions().forEach((position, index) => {
    const marker = new window.google.maps.Marker({
      position,
      map: Map,
      draggable: true,
      zIndex: 100,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 6,
        fillColor: '#FFFFFF',
        fillOpacity: 1,
        strokeColor: '#003832',
        strokeWeight: 2,
      },
    });

    marker.addListener('drag', (event) => {
      moveVertex(index, event.latLng);
      if (isRectangle) syncMarkers(index);
    });

    marker.addListener('dragend', () => {
      syncMarkers();
      handler(getOverlayCoordinates(overlay, shapeType));
    });

    markers.push(marker);
  });

  return () =>
    markers.forEach((marker) => {
      window.google.maps.event.clearInstanceListeners(marker);
      marker.setMap(null);
    });
};

export const extractCoordinates = (object, type) => {
  if (type === 'rectangle') {
    const ne = object.getBounds().getNorthEast().toJSON();
    const sw = object.getBounds().getSouthWest().toJSON();
    const nw = { lat: ne.lat, lng: sw.lng };
    const se = { lat: sw.lat, lng: ne.lng };

    return [nw, ne, se, sw];
  } else if (type === 'polygon' || type === 'polyline') {
    return object
      .getPath()
      .getArray()
      .map((value) => value.toJSON());
  }
};
